import { customerVersionState } from '@noconflict/contracts';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CustomerVersion,
  useActiveRun,
  useScriptVersions,
  useVersionsBought,
} from '../../entities/script-version';
import { DeleteVersionButton } from '../../features/versions/delete-version';
import { UploadVersion } from '../../features/versions/upload-version';
import { useFormat } from '../../shared/i18n/format';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/card';
import { ErrorState } from '../../shared/ui/error-state';
import { RevisionPill } from '../../shared/ui/revision-pill';
import { Skeleton } from '../../shared/ui/skeleton';
import { StatusBadge } from '../../shared/ui/status-badge';

/** An ordered script takes versions until it is delivered or cancelled. */
const TAKES_VERSIONS = new Set(['ordered', 'in_progress']);

/**
 * The customer's Versions card on an ordered script project: how many of the versions bought are used, a row
 * per version (Received until staff confirm it, Reviewed after with its revision pills; never a staff
 * status), **Delete** on the newest
 * one while it is not reviewed and no run is active, and the upload while a slot is free. Upload and delete are
 * shown to someone who may edit the project; the worker decides.
 */
export function VersionsCard({
  projectId,
  projectStatus,
  canEdit,
}: {
  projectId: string;
  projectStatus: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation('project');
  const list = useScriptVersions(projectId);
  const bought = useVersionsBought(projectId);
  const activeRun = useActiveRun(projectId);
  const queries = [list, bought, activeRun];
  const headingId = useId();

  const failed = queries.find((q) => q.isError);
  const body = failed ? (
    <ErrorState
      size="compact"
      title={t('versions.errorTitle')}
      description={failed.error?.message}
      onRetry={() => {
        for (const q of queries) void q.refetch();
      }}
    />
  ) : list.isPending || bought.isPending || activeRun.isPending ? (
    <section aria-busy="true" aria-label={t('versions.loading')} className="grid gap-2">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </section>
  ) : null;

  const versions = list.data ?? [];
  const used = Math.max(versions.length - 1, 0);
  const slots = bought.data ?? 0;
  const editable = canEdit && TAKES_VERSIONS.has(projectStatus);
  const newest = versions.at(-1);
  const deletable =
    editable &&
    newest !== undefined &&
    newest.version_number > 1 &&
    newest.status !== 'confirmed' &&
    activeRun.data === false;

  return (
    <Card role="region" aria-labelledby={headingId}>
      <CardHeader>
        <CardTitle asChild className="font-sans">
          <h2 id={headingId}>{t('versions.title')}</h2>
        </CardTitle>
        {body ? null : (
          <CardDescription>{t('versions.used', { used, count: slots })}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="grid gap-6">
        {body ?? (
          <>
            <ol aria-label={t('versions.listLabel')} className="grid divide-y divide-border">
              {versions.map((version) => (
                <VersionRow
                  key={version.id}
                  version={version}
                  action={
                    deletable && version.id === newest?.id ? (
                      <DeleteVersionButton
                        projectId={projectId}
                        versionId={version.id}
                        number={version.version_number}
                      />
                    ) : null
                  }
                />
              ))}
            </ol>
            {editable ? (
              used < slots ? (
                <UploadVersion projectId={projectId} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {slots === 0 ? t('versions.noneBought') : t('versions.limit')}
                </p>
              )
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function VersionRow({ version, action }: { version: CustomerVersion; action: React.ReactNode }) {
  const { t } = useTranslation('project');
  const { formatDateTime } = useFormat();
  const state = customerVersionState(version.status);
  const date = formatDateTime(version.created_at);
  const { revisions } = version;
  return (
    <li className="grid gap-2 py-3 first:pt-0 last:pb-0 sm:flex sm:items-center sm:gap-4">
      <div className="grid min-w-0 flex-1 gap-0.5">
        <p className="flex flex-wrap gap-x-2">
          <span className="font-bold text-heading">
            {version.version_number === 1
              ? t('versions.original')
              : t('versions.number', { number: version.version_number })}
          </span>
          {version.label ? (
            <span className="break-words">{t('versions.withLabel', { label: version.label })}</span>
          ) : null}
        </p>
        {revisions && revisions.labels.length > 0 ? (
          <ul className="flex flex-wrap gap-1" aria-label={t('versions.revisions')}>
            {revisions.labels.map((label) => (
              <li key={label ?? ''}>
                <RevisionPill scheme={revisions.scheme} label={label} format={revisions.format} />
              </li>
            ))}
          </ul>
        ) : null}
        <p className="text-sm text-muted-foreground">
          {version.uploaderName
            ? t('versions.uploadedBy', { date, name: version.uploaderName })
            : t('versions.uploaded', { date })}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <StatusBadge tone={state === 'reviewed' ? 'success' : 'info'} size="compact">
          {t(`versions.state.${state}`)}
        </StatusBadge>
        {action}
      </div>
    </li>
  );
}
